import type { DrawingShareReadResponse } from "@excalidraw-ms/shared";
import * as backendClient from "../api/backendClient";

const PEOPLE_SHARING_KINDS = new Set(["none", "link", "recipient"]);

function validatePeopleSharingResponse(value: unknown): DrawingShareReadResponse {
  const peopleSharingKind = (value as { peopleSharingKind?: unknown } | null)?.peopleSharingKind;
  if (
    !value ||
    typeof value !== "object" ||
    typeof (value as { isShared?: unknown }).isShared !== "boolean" ||
    typeof peopleSharingKind !== "string" ||
    !PEOPLE_SHARING_KINDS.has(peopleSharingKind) ||
    !Array.isArray((value as { links?: unknown }).links)
  ) {
    throw new Error("Invalid people sharing response");
  }
  return value as DrawingShareReadResponse;
}

export class PeopleLinkPreparationError extends Error {
  constructor(
    public readonly sharing: DrawingShareReadResponse,
    public readonly retry: () => Promise<DrawingShareReadResponse>,
    options: ErrorOptions,
  ) {
    super("People invitation link could not be prepared", options);
    this.name = "PeopleLinkPreparationError";
  }
}

async function prepareRecipientLink(
  token: string,
  drawingId: string,
  sharing: DrawingShareReadResponse,
): Promise<DrawingShareReadResponse> {
  const link = await backendClient.createDrawingShare(token, drawingId, "viewer");
  return { ...sharing, links: [link] };
}

export async function readPeopleSharing(
  token: string,
  drawingId: string,
  onStatusResolved: (
    isShared: boolean,
    kind: DrawingShareReadResponse["peopleSharingKind"],
  ) => boolean,
): Promise<DrawingShareReadResponse> {
  const sharing = validatePeopleSharingResponse(
    await backendClient.getDrawingShare(token, drawingId),
  );
  if (!onStatusResolved(sharing.peopleSharingKind !== "none", sharing.peopleSharingKind)) {
    return sharing;
  }
  if (sharing.peopleSharingKind !== "recipient" || sharing.links.length > 0) return sharing;

  // Recipient access can remain after all invitation permissions are gone.
  // Creating a replacement invitation must not silently grant edit access.
  try {
    return await prepareRecipientLink(token, drawingId, sharing);
  } catch (error) {
    throw new PeopleLinkPreparationError(
      sharing,
      () => prepareRecipientLink(token, drawingId, sharing),
      { cause: error },
    );
  }
}
